import React, {useEffect, useId, useRef, useState} from 'react';
import {Overlay} from 'react-bootstrap';

/**
 * Auswahlliste im Stil des Auftrags-Editors — Ersatz für das native `<select>`
 * der Filterzeile (Rabattgruppe, Stammlager). Browsereigene Bedienelemente
 * sind im Haus nicht zugelassen; gerendert wird deshalb eigenes Markup mit
 * `role="combobox"`/`role="listbox"` und vollständiger Tastaturbedienung
 * (Pfeile, Pos1/Ende, Eingabe, Esc).
 *
 * Optisch übernimmt der Auslöser die Klasse `.colf` des Originals, damit die
 * Filterzeile unverändert aussieht.
 *
 * Trägt ein Eintrag ein Feld `group`, wird beim Wechsel der Gruppe eine
 * Zwischenüberschrift eingezogen — der Ersatz für `<optgroup>` (Versandartikel
 * nach Palette / Fracht / Paket).
 *
 * @param {string|number} value
 * @param {{value: string|number, label: string, group?: string}[]} options
 * @param {(value: string|number) => void} onChange
 * @param {string} ariaLabel
 * @param {string} [className="colf"]
 * @param {boolean} [disabled]
 */
export default function OeSelect({value, options, onChange, ariaLabel, className = 'colf', disabled = false}) {
    const generatedId = useId();
    const instanceId = `oe-select-${generatedId}`;
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const selectedIndex = options.findIndex((opt) => String(opt.value) === String(value ?? ''));
    const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

    const openList = (startIndex) => {
        setActiveIndex(startIndex >= 0 ? startIndex : 0);
        setOpen(true);
    };

    const closeList = (refocus = true) => {
        setOpen(false);
        if (refocus) {
            triggerRef.current?.focus();
        }
    };

    const commitIndex = (index) => {
        const opt = options[index];
        if (opt) {
            onChange(opt.value);
        }
        closeList();
    };

    const handleKeyDown = (e) => {
        if (disabled) {
            return;
        }
        if (!open) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
                openList(selectedIndex);
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => Math.min(options.length - 1, i + 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => Math.max(0, i - 1));
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(options.length - 1);
                break;
            case 'Enter':
                e.preventDefault();
                commitIndex(activeIndex);
                break;
            case 'Escape':
                e.preventDefault();
                closeList();
                break;
            case 'Tab':
                closeList(false);
                break;
            default:
                break;
        }
    };

    // Beim Blättern per Tastatur den aktiven Eintrag in Sicht halten.
    useEffect(() => {
        if (!open) {
            return;
        }
        listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({block: 'nearest'});
    }, [open, activeIndex]);

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                id={instanceId}
                className={`${className} oe-select-trigger`}
                disabled={disabled}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={`${instanceId}-listbox`}
                aria-activedescendant={open && activeIndex >= 0 ? `${instanceId}-option-${activeIndex}` : undefined}
                aria-label={ariaLabel}
                onClick={() => (open ? closeList() : openList(selectedIndex))}
                onKeyDown={handleKeyDown}
            >
                <span className="oe-select-value">{selectedOption ? selectedOption.label : ' '}</span>
                <span className="oe-select-caret" aria-hidden="true">▾</span>
            </button>

            <Overlay target={triggerRef} show={open} placement="bottom-start" flip rootClose onHide={() => closeList(false)}>
                {(overlayProps) => (
                    <div
                        {...overlayProps}
                        id={`${instanceId}-listbox`}
                        role="listbox"
                        aria-label={ariaLabel}
                        tabIndex={-1}
                        ref={(node) => {
                            listRef.current = node;
                            if (typeof overlayProps.ref === 'function') {
                                overlayProps.ref(node);
                            }
                        }}
                        className="oe-select-list"
                        style={{
                            ...overlayProps.style,
                            minWidth: triggerRef.current?.offsetWidth || 120,
                        }}
                        onKeyDown={handleKeyDown}
                    >
                        {options.map((opt, index) => (
                            <React.Fragment key={String(opt.value)}>
                                {opt.group && opt.group !== (options[index - 1] || {}).group ? (
                                    <div className="oe-select-group" role="presentation">{opt.group}</div>
                                ) : null}
                                <div
                                    id={`${instanceId}-option-${index}`}
                                    data-index={index}
                                    role="option"
                                    aria-selected={index === selectedIndex}
                                    className={`oe-select-option${index === activeIndex ? ' active' : ''}`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        onChange(opt.value);
                                        closeList();
                                    }}
                                >
                                    {opt.label}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </Overlay>
        </>
    );
}
